<template>
  <div class="categories-component-container list-page-wrapper">
    <page-header :breadcrumbs="breadcrumbs"></page-header>
    <div class="heading list-page-header">
      <span
        v-if="parentCategory.id && parentCategory.imageAttachmentId"
        class="category-icon-wrapper"
      >
        <img
          :src="getCategoryImageUrl()"
          :alt="`icon for ${parentCategory.name}`"
          class="category-icon"
        />
      </span>
      <span>{{ title }}</span>
    </div>
    <div class="list-tabs">
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="unansweredFlashcardStatus"
        :count="flashcardCounts[currentScope]['unanswered']"
      />
      <span class="mobile-flex-1">
        <filter-tab
          :status="redFlashcardStatus"
          :count="flashcardCounts[currentScope]['red']"
          @click="finishTour"
        >
        </filter-tab>
        <tour-tip
          ref="tourTipFilterRef"
          :auto-hide="false"
          :tourtip-name="'filters'"
          :offset="10"
        ></tour-tip>
      </span>
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="yellowFlashcardStatus"
        :count="flashcardCounts[currentScope]['yellow']"
      />
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="greenFlashcardStatus"
        :count="flashcardCounts[currentScope]['green']"
      />
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="bookmarkedFlashcardStatus"
        :count="flashcardCounts[currentScope]['bookmarked']"
      />
    </div>
    <div
      v-disableable="{disabled: $store.getters['session/onTour']}"
      class="list-container tabbed"
    >
      <list-row
        :key="`${resourceCategories.length}-all`"
        class="all-categories-list-row"
        @row-clicked="handleAllRowClick"
      >
        <div class="center-container">
          <div class="name">
            <lock-button
              v-if="parentCategory.id && !$access($store, 'category', parentCategory.id)"
              :resource="{ type: 'category', id: parentCategory.id }"
              :source="{ type: 'Locked Category', value: parentCategory.id }"
            />
            <purchase-order-preview
              v-if="user.previewer"
              :resource="parentCategory"
            />
            <span
              :class="{ 'label-disabled': categoryCounter(flashcardCounts[currentScope]) <= 0 }"
              class="text-xs font-medium text-brand-primary"
            >
              {{ allName }}
            </span>
          </div>
        </div>
        <counter :count="categoryCounter(flashcardCounts[currentScope])" primary />
      </list-row>
      <template v-for="category in scopedCategories" :key="category.id">
        <list-row
          class="category-list-row"
          @row-clicked="handleCategoryRowClick(category)"
        >
          <div class="center-container">
            <span
              v-if="category.imageAttachmentId && categoryImages[category.id]"
              class="category-image"
            >
              <img :src="categoryImages[category.id]" />
            </span>
            <div class="name">
              <lock-button
                v-if="!$access($store, 'category', category.id)"
                :resource="{ type: 'category', id: category.id }"
                :source="{ type: 'Locked Category', value: category.id }"
              />
              <purchase-order-preview
                v-if="user.previewer"
                :resource="category"
              />
              {{ category.name }}
            </div>
          </div>
          <counter :count="categoryCounter(flashcardCounts[category.id])" />
        </list-row>
      </template>
    </div>
  </div>
</template>

<script>
import AuthenticationMixin from '@/mixins/authentication';
import CategoriesMixin from '@/mixins/categories';
import QuestionsUiV2Mixin from '@/mixins/questions-ui-v2';
import Counter from '@components/singletons/counter';
import FilterTab from '@components/categories/filter-tab';
import GlobalMixin from '@/mixins/global';
import LockButton from '@components/singletons/lock-button';
import ListRow from '@components/ui/listRow';
import PageHeader from '@components/singletons/pageHeader';
import TourTip from '@components/singletons/tour-tip';
import PurchaseOrderPreview from '@components/singletons/purchaseOrderPreview';

import { capitalize } from '@/utils';
import { setChecklistData, COMPLETE_TOOLTIPS_TOUR_KEY } from '@/utils/checklist';
export default {
  components: { Counter, FilterTab, ListRow, LockButton, PageHeader, TourTip, PurchaseOrderPreview },
  mixins: [AuthenticationMixin, CategoriesMixin, GlobalMixin, QuestionsUiV2Mixin],
  computed: {
    allName() {
      let resourceName;
      if (this.categoryTypeId) {
        resourceName = this.categoryType.displayNamePlural;
      } else if (this.parentCategory.name) {
        resourceName = this.parentCategory.name;
      } else {
        const data = this.flashcardCounts['all'];
        let singular = false;
        if (this.flashcardStatusFilter.id && this.flashcardStatusFilter != this.unansweredFlashcardStatus) {
          singular = data[this.flashcardStatusFilter.name] === 1;
        } else {
          singular = data['answered'] === data['count'] - 1;
        }
        let word = singular ? 'Item' : 'Items';
        if (this.currentResource.resourceId && this.$store.getters['categoryTypes/categoryTypes'].find(ct => ct.id === this.currentResource.resourceId)) {
          let ct = this.$store.getters['categoryTypes/categoryTypes'].find(ct => ct.id === this.currentResource.resourceId);
          word = singular ? ct.displayNameSingular : ct.displayNamePlural;
        }
        resourceName = capitalize(word);
      }

      if (this.categoryTypeId || this.parentCategory.name) {
        return `All ${resourceName}`;
      } else {
        return `Study All ${resourceName}`;
      }
    },
    allRoute() {
      let category = this.parentCategory.id ? this.parentCategory : {id: 'all'};
      if(!this.categoryFinished(category)) {
        return { name: 'categories#flashcard', params: { categoryId: category.id }, query: this.currentQuery };
      } else {
        return { query: this.currentQuery};
      }
    },
    breadcrumbs() {
      if (!this.parentCategory.id) return [];
      let categories = this.parentCategory.path.split('.').map(id => this.categories[id]);
      let crumbs = categories.map(c => {
        return { name: c.name, route: {name: 'categories#index', params: { categoryId: c.id}, query: this.currentQuery}};
      });
      return crumbs;
    },
    bookmarkedFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/bookmarkedFlashcardStatus'];
    },
    categoryImages() {
        const images = {};
        this.scopedCategories.forEach(c => {
          const image = this.$store.getters['attachments/get'][c.imageAttachmentId];
          images[c.id] = image ? image.url : '';
        });
        return images;
      },
    categoryTypeId() {
      return this.$route.params['categoryTypeId'];
    },
    categoryType() {
      const categoryTypeId = this.$route.params['categoryTypeId'];
      if (categoryTypeId) {
        return this.$store.getters['categoryTypes/all'][categoryTypeId];
      }
      return null;
    },
    currentScope() {
      return this.parentCategory.id || 'all';
    },
    flashcardStatusFilter() {
      return this.$store.getters['flashcardStatuses/flashcardStatusFilter'];
    },
    greenFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/greenFlashcardStatus'];
    },
    imageAttachmentIds() {
      return this.scopedCategories.map(c => c.imageAttachmentId).filter(id => id);
    },
    isParentCategorySimulation() {
      const categoryTypeId = this.parentCategory.categoryTypeId;
      const simulationTypeIds = this.$store.getters['categoryTypes/testSimulationCategoryTypes'].map(t => t.id);
      return simulationTypeIds.includes(categoryTypeId);
    },
    parentCategory() {
      return this.categories[this.$route.params.categoryId] || {};
    },
    redFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/redFlashcardStatus'];
    },
    title() {
      if (this.parentCategory.name) {
        return this.parentCategory.name;
      } else {
        return this.currentResource.name;
      }
    },
    unansweredFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'];
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
    yellowFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/yellowFlashcardStatus'];
    },
  },
  watch: {
    '$route.params.categoryId': {
      immediate: true,
      handler: function(id) {
        if (this.isParentCategorySimulation) {
          this.$router.push({
            name: 'sims#show',
            params: {
              simId: this.parentCategory.id,
            },
          });
        }
        if (this.parentCategory.id) {
          this.$analytics.events.viewedCategory(this.$store, this.parentCategory);
        }
      },
    },
    '$route': {
      immediate: true,
      handler: function(to, from) {
        // if a user switches directly between flashcard nav items, reset filter
        if (to && from && to.query.nav !== from.query.nav) {
          this.$store.commit('flashcardStatuses/setFlashcardStatusFilter');
        }
      },
    },
    parentCategory: {
      handler: function() {
        this.fetchCategoryAttachment();
      },
      immediate: true,
    },
  },
  mounted() {
    this.$store.commit('flashcardStatuses/setFlashcardStatusFilter');
    this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'flashcards']);
    if (this.imageAttachmentIds.length > 0) {
        this.$store.dispatch('attachments/fetch', { ids: this.imageAttachmentIds });
      }
    this.fetchCategoryAttachment();
  },
  methods: {
    categoryCounter(data) {
      if (this.flashcardStatusFilter.id && this.flashcardStatusFilter != this.unansweredFlashcardStatus) {
        return data[this.flashcardStatusFilter.name];
      }
      return data['unanswered'];
    },
    categoryFinished(category) {
      const data = this.flashcardCounts[category.id];

      if(this.flashcardStatusFilter.id && this.flashcardStatusFilter != this.unansweredFlashcardStatus) {
        return data[this.flashcardStatusFilter.name] < 1;
      } else {
        return data['answered'] >= data['count'];
      }
    },
    categoryRoute(category) {
      if (category.children.length > 0) {
        return { name: 'categories#index', params: { categoryId: category.id }, query: this.currentQuery };
      } else {
        if (this.$access(this.$store, 'category', category.id) && !this.categoryFinished(category)) {
          if (this.questionUiV2Enabled) {
            return { name: 'categories#flashcard-v2', params: { categoryId: category.id }, query: this.currentQuery };
          }
          return { name: 'categories#flashcard', params: { categoryId: category.id }, query: this.currentQuery };
        } else {
          return { query: this.currentQuery};
        }
      }
    },
    finishTour() {
      if (this.$store.getters['session/onTour']) {
        this.$refs.tourTipFilterRef.open = false;
        const self = this;
        setTimeout(function() {
          self.$store.commit('session/setOnTour', false);
          self.$store.commit('session/setTourType', '');
          setChecklistData(self.$store, COMPLETE_TOOLTIPS_TOUR_KEY, self.$notify);
          self.$modal.show('questions-finish-tour-modal');
        }, 1500);
      }
    },
    handleAllCategoryType() {
      const route = {
        name: 'categoryTypes#flashcard-v2',
        params: {
          categoryTypeId: this.categoryTypeId,
        },
        query: this.currentQuery,
      };
      this.$router.push(route);
    },
    handleAllRowClick() {
      if (this.categoryTypeId) {
        this.handleAllCategoryType();
        return;
      }
      const locked = this.parentCategory.id && !this.$access(this.$store, 'category', this.parentCategory.id);
      if (locked) {
        this.showUpsell(this.parentCategory);
      } else {
        this.$router.push(this.allRoute);
      }
    },
    handleCategoryRowClick(category) {
      const locked = !this.$access(this.$store, 'category', category.id) && category.children.length < 1;
      if (locked) {
        this.showUpsell(category);
      } else {
        const route = this.categoryRoute(category);
        this.$router.push(route);
      }
    },
    fetchCategoryAttachment() {
      if (this.parentCategory.imageAttachmentId) {
        this.$store.dispatch('attachments/fetch', { ids: [this.parentCategory.imageAttachmentId] });
      }
    },
    getCategoryImageUrl() {
      const attachmentId = this.parentCategory.imageAttachmentId;
      const image = this.$store.getters['attachments/get'][attachmentId];
      return image ? image.url : '';
    },
    showUpsell(category) {
      if (!this.$access(this.$store, 'category', category.id) && category.children.length < 1) {
        this.$store.dispatch('ui/toggleUpsellModal', {
          show: true,
          resource: { type: 'category', id: category.id },
          source: { type: 'Locked Category', value: category.id },
        });
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  .categories-component-container {
    .list-tabs {
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }

  .label-disabled {
    color: $grey-3;
  }

  .category-icon-wrapper {
    display: flex;
    align-items: center;
  }

  .category-icon {
    height: rem-calc(48);
    object-fit: cover;
    object-position: center;
    border-radius: 0.25rem;
    mix-blend-mode: multiply;
  }

  .category-image {
    width: rem-calc(40);
    height: rem-calc(40);
    mix-blend-mode: multiply;
    margin-right: 1rem;
  }

  .name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :deep(.trigger) {
    display: block !important;
  }

  .mobile-flex-1 {
    flex: 1;

    @include breakpoint(desktop) {
      flex: unset;
    }
  }
</style>
